import { normalizeCarolinoCategoria } from '@/utils/carolinoCategorias';
import { fixMojibake } from '@/utils/textEncoding';

export type BackofficeRole =
  | 'admin_backoffice'
  | 'viewer_backoffice'
  | null;

export interface AuthMeResponse {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
  cpf?: string | null;
  company_name?: string | null;
  cnpj?: string | null;
  email?: string | null;
  phone?: string | null;
  person_type?: string | null;
  created_at?: number | string;
  backoffice_role: BackofficeRole;
  department?: string | null;
  job_title?: string | null;
  display_name?: string | null;
  must_change_password: boolean;
  /** Quando true, mantém login no portal mas bloqueia backoffice. */
  internal_disabled?: boolean;
  /** Claim JWT do login — false quando interno desativado. */
  is_backoffice?: boolean;
}

export interface BackofficePingResponse {
  ok: boolean;
  user_id: number;
  backoffice_role: Exclude<BackofficeRole, null>;
  display_name?: string | null;
  internal_disabled?: boolean;
}

export interface ChangePasswordBody {
  current_password: string;
  new_password: string;
}

export interface XanoPaginatedResponse<T> {
  items: T[];
  curPage?: number;
  page?: number;
  per_page?: number;
  nextOffset?: number | null;
  /** Total de registros (Xano: itemsTotal ou total). */
  total?: number;
  itemsTotal?: number;
  count?: number;
  /** Número de páginas — não confundir com total de itens. */
  pageTotal?: number;
}

export type PortalAgendaBlockType = 'full_day' | 'partial_range';

export interface PortalAgendaBlock {
  id: number;
  date: string;
  type: PortalAgendaBlockType;
  start_time: string | null;
  end_time: string | null;
  reason: string | null;
  active: boolean;
  created_at?: number;
  /** Preenchidos ao desbloquear. Exigem colunas no Xano. */
  unlock_reason?: string | null;
  unlock_justification?: string | null;
  unlocked_at?: number | null;
}

export type AgendaBlock = PortalAgendaBlock & {
  block_type?: PortalAgendaBlockType;
  label?: string | null;
};

export interface PortalCustomer {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  company_name: string | null;
  cpf: string | null;
  cnpj: string | null;
  person_type: string | null;
  id_sf_account: string | null;
  id_sf_contact: string | null;
  created_at?: number;
}

export type CustomersListResponse = XanoPaginatedResponse<PortalCustomer>;

export interface PatchCustomerBody {
  cpf?: string;
  cnpj?: string;
  person_type?: string;
  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  id_sf_account?: string | null;
  id_sf_contact?: string | null;
}

function isCustomerPersonTypePJ(personType?: string | null): boolean {
  return (personType || 'PF').toUpperCase() === 'PJ';
}

/** Payload PATCH customers — inclui aliases e compat com Xano que expõe só `cnpj`. */
export function toCustomerPatchApiBody(
  body: PatchCustomerBody
): Record<string, unknown> {
  const isPJ = isCustomerPersonTypePJ(body.person_type);
  const payload: Record<string, unknown> = {};

  const setText = (key: string, value?: string | null) => {
    if (value === undefined) return;
    payload[key] = value;
    const upper = key.toUpperCase();
    if (upper !== key) payload[upper] = value;
  };

  setText('first_name', body.first_name);
  setText('last_name', body.last_name);
  setText('email', body.email);
  setText('phone', body.phone);
  setText('person_type', body.person_type);
  setText('company_name', body.company_name);

  const sfAccount = body.id_sf_account?.trim() || null;
  const sfContact = body.id_sf_contact?.trim() || null;
  setText('id_sf_account', sfAccount);
  setText('id_sf_contact', sfContact);
  // Aliases usados em alguns endpoints / campos legados do Xano
  payload.sf_account_id = sfAccount;
  payload.sf_contact_id = sfContact;
  payload.Id_SF_Account = sfAccount;
  payload.Id_SF_Contact = sfContact;

  if (isPJ) {
    setText('cnpj', body.cnpj ?? null);
    setText('cpf', null);
  } else {
    const cpf = body.cpf ?? null;
    setText('cpf', cpf);
    // Endpoint legado do backoffice costuma aceitar documento PF apenas em `cnpj`.
    setText('cnpj', cpf);
  }

  return payload;
}

export function normalizePortalCustomer(raw: Record<string, unknown>): PortalCustomer {
  const id = Number(raw.id ?? raw.user_id ?? raw.ID ?? 0);
  const str = (key: string, alt?: string) => {
    const v = raw[key] ?? (alt ? raw[alt] : undefined);
    if (v == null || v === '') return null;
    return String(v);
  };

  const cpfRaw = str('cpf', 'CPF');
  const cnpjRaw = str('cnpj', 'CNPJ');
  const companyName =
    str('company_name', 'companyName') ?? str('COMPANY_NAME');
  const personTypeRaw =
    str('person_type', 'personType') ?? str('PERSON_TYPE');

  const cpfDigits = (cpfRaw ?? '').replace(/\D/g, '');
  const cnpjDigits = (cnpjRaw ?? '').replace(/\D/g, '');

  let person_type = personTypeRaw;
  if (!person_type) {
    if (cnpjDigits.length === 14 || companyName) {
      person_type = 'PJ';
    } else if (cpfDigits.length === 11 || cnpjDigits.length === 11) {
      person_type = 'PF';
    } else {
      person_type = 'PF';
    }
  }

  const isPJ = person_type.toUpperCase() === 'PJ';
  let cpf = cpfRaw;
  let cnpj = cnpjRaw;

  if (!isPJ) {
    if (!cpf && cnpjDigits.length === 11) {
      cpf = cnpjRaw;
    }
    if (cnpjDigits.length === 11) {
      cnpj = null;
    }
  }

  return {
    id,
    first_name: str('first_name', 'firstName') ?? str('FIRST_NAME'),
    last_name: str('last_name', 'lastName') ?? str('LAST_NAME'),
    email: str('email', 'EMAIL'),
    phone: str('phone', 'PHONE') ?? str('telefone'),
    company_name: companyName,
    cpf,
    cnpj: isPJ ? cnpj : cnpjDigits.length === 14 ? cnpj : null,
    person_type,
    id_sf_account:
      str('id_sf_account', 'idSfAccount') ??
      str('ID_SF_ACCOUNT') ??
      str('sf_account_id', 'sfAccountId') ??
      str('account_sf_id') ??
      str('Id_SF_Account'),
    id_sf_contact:
      str('id_sf_contact', 'idSfContact') ??
      str('ID_SF_CONTACT') ??
      str('sf_contact_id', 'sfContactId') ??
      str('contact_sf_id') ??
      str('Id_SF_Contact'),
    created_at:
      typeof raw.created_at === 'number'
        ? raw.created_at
        : typeof raw.CREATED_AT === 'number'
          ? raw.CREATED_AT
          : undefined,
  };
}

export function portalCustomerId(c: PortalCustomer): number {
  return c.id;
}

export interface InternalUser {
  user_id?: number;
  id?: number;
  email: string | null;
  backoffice_role: Exclude<BackofficeRole, null>;
  department: string | null;
  job_title: string | null;
  display_name: string | null;
  must_change_password: boolean;
  internal_disabled: boolean;
  created_at?: number | string | null;
  updated_at?: number | string | null;
}

export type InternalUsersListResponse = XanoPaginatedResponse<InternalUser>;

export interface CreateInternalUserBody {
  email: string;
  password?: string;
  display_name: string;
  department?: string;
  job_title?: string;
  backoffice_role: Exclude<BackofficeRole, null>;
  /** Quando true, vincula conta portal existente sem enviar e-mail de senha temporária. */
  link_existing_portal_user?: boolean;
  /** ID do `user` portal existente — obrigatório ao vincular cliente como interno. */
  user_id?: number;
}

export interface CreateInternalUserResponse extends InternalUser {
  /** Não retornado na criação — senha enviada por e-mail pelo Xano. */
  temporary_password?: string | null;
}

/** Payload para promover cliente portal existente — com e-mail (exigido pelo Xano), sem senha. */
export interface LinkPortalUserBody {
  user_id: number;
  link_existing_portal_user: true;
  email: string;
  display_name: string;
  department?: string;
  job_title?: string;
  backoffice_role: Exclude<BackofficeRole, null>;
}

export interface EstablishmentFreightConfig {
  id: number;
  frete_fixo_km?: number;
  frete_fixo_valor?: number;
  valor_excedente_por_km?: number;
  ativo?: boolean;
}

export interface EstablishmentCoverage {
  id: number;
  cep_de: string;
  cep_ate: string;
  opcao_frete?: string;
  valor_frete?: number;
  ativo?: boolean;
}

export interface EstablishmentMargin {
  id: number;
  km_de: number;
  km_ate: number;
  valor: number;
  ativo?: boolean;
}

export interface EstablishmentSummary {
  id: number;
  nome: string;
  endereco?: string;
  cep?: string;
  principal?: boolean;
  ativo?: boolean;
}

export interface EstablishmentDetail extends EstablishmentSummary {
  origin_lat?: number;
  origin_lng?: number;
  frete_padrao?: number;
  freight_config?: EstablishmentFreightConfig | null;
  coverage?: EstablishmentCoverage[];
  margins?: EstablishmentMargin[];
}

export interface ExtraService {
  id: number;
  Nome?: string;
  nome?: string;
  Valor?: number;
  valor?: number;
  Ativo?: boolean;
  ativo?: boolean;
  Descricao?: string;
  Tipo?: string;
}

export interface PortalExtraService {
  id: number;
  nome?: string;
  Nome?: string;
  valor?: number;
  Valor?: number;
  ativo?: boolean;
  Ativo?: boolean;
  descricao?: string;
  Descricao?: string;
}

export interface CsatDistributionItem {
  score?: number;
  nota?: number;
  rating?: number;
  value?: number;
  count?: number;
  total?: number;
  quantidade?: number;
}

export interface CsatReport {
  count?: number;
  total?: number;
  avg_score?: number;
  average?: number;
  media?: number;
  distribution?:
    | Record<string, number>
    | Array<CsatDistributionItem>;
}

export interface CsatResponse {
  id: number;
  order_id?: number;
  pedido_id?: number;
  score: number;
  comment?: string | null;
  created_at?: number;
  /** Nome do cliente (API / join / metadata). */
  cliente?: string | null;
  customer_name?: string | null;
  client_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  /** Empresa / razão social. */
  empresa?: string | null;
  company_name?: string | null;
  company?: string | null;
  user_id?: number | null;
  metadata?: Record<string, unknown> | null;
  user?: {
    first_name?: string | null;
    last_name?: string | null;
    company_name?: string | null;
    company?: string | null;
    email?: string | null;
  } | null;
}

export function internalUserId(u: InternalUser): number {
  return u.user_id ?? u.id ?? 0;
}

export function normalizeInternalUser(raw: Record<string, unknown>): InternalUser {
  const nestedUser =
    raw.user && typeof raw.user === 'object'
      ? (raw.user as Record<string, unknown>)
      : null;

  const str = (...keys: string[]) => {
    for (const key of keys) {
      const v = raw[key] ?? nestedUser?.[key];
      if (v == null || v === '') continue;
      return String(v);
    }
    return null;
  };

  const userId = Number(
    raw.user_id ?? raw.id ?? nestedUser?.id ?? nestedUser?.user_id ?? 0
  );

  const roleRaw = str('backoffice_role', 'BACKOFFICE_ROLE');
  const backoffice_role =
    roleRaw === 'admin_backoffice' || roleRaw === 'viewer_backoffice'
      ? roleRaw
      : 'viewer_backoffice';

  return {
    user_id: userId || undefined,
    id: userId || undefined,
    email: str('email', 'EMAIL') ?? nestedUser?.email?.toString() ?? null,
    display_name: str('display_name', 'displayName', 'DISPLAY_NAME'),
    department: str('department', 'DEPARTMENT'),
    job_title: str('job_title', 'jobTitle', 'JOB_TITLE'),
    backoffice_role,
    must_change_password: Boolean(raw.must_change_password ?? false),
    internal_disabled: Boolean(raw.internal_disabled ?? false),
    created_at:
      (raw.created_at as number | string | undefined) ??
      (nestedUser?.created_at as number | string | undefined),
    updated_at: raw.updated_at as number | string | undefined,
  };
}

export function upsertInternalUser(
  list: InternalUser[],
  user: InternalUser
): InternalUser[] {
  const id = internalUserId(user);
  if (!id) return [user, ...list];

  const idx = list.findIndex(item => internalUserId(item) === id);
  if (idx < 0) return [user, ...list];

  const next = [...list];
  next[idx] = { ...next[idx], ...user };
  return next;
}

export function extraServiceName(s: ExtraService | PortalExtraService): string {
  return s.Nome ?? s.nome ?? `Serviço ${s.id}`;
}

export function extraServicePrice(s: ExtraService | PortalExtraService): number {
  const v = s.Valor ?? s.valor;
  return typeof v === 'number' ? v : 0;
}

export function extraServiceActive(s: ExtraService | PortalExtraService): boolean {
  const a = s.Ativo ?? s.ativo;
  return a !== false;
}

// --- Configurações (portal categories + cálculo base Carolino) ---

export interface PortalCategory {
  id: number;
  slug: string;
  label: string;
  display_order: number;
  active: boolean;
  created_at?: number;
}

/** Linha da tabela calculo_base (motor Carolino). */
export interface CalculoBaseRow {
  id: number;
  tipo_do_evento: string;
  categoria: string;
  familia: string;
  variacao: number;
  qtd_un_por_pes: number;
}

export interface CreateCalculoBaseBody {
  tipo_do_evento: string;
  categoria: string;
  familia: string;
  variacao: number;
  qtd_un_por_pes: number;
}

export type PatchCalculoBaseBody = Partial<CreateCalculoBaseBody>;

/** Payload para POST/PATCH — colunas da tabela calculo_base no Xano. */
export function toCalculoBaseApiBody(
  body: CreateCalculoBaseBody | PatchCalculoBaseBody
): Record<string, string | number> {
  const payload: Record<string, string | number> = {};

  if (body.tipo_do_evento !== undefined) {
    payload.TIPO_DO_EVENTO = body.tipo_do_evento;
  }
  if (body.categoria !== undefined) {
    payload.CATEGORIA = body.categoria;
  }
  if (body.familia !== undefined) {
    payload.FAMILIA = body.familia;
  }
  if (body.variacao !== undefined) {
    payload.VARIACAO = body.variacao;
  }
  if (body.qtd_un_por_pes !== undefined) {
    payload.QTD_UN_POR_PES = body.qtd_un_por_pes;
  }

  return payload;
}

/** Query params para filtros GET calculo-base. */
export function toCalculoBaseApiQuery(
  query?: Record<string, string | number>
): Record<string, string | number> | undefined {
  if (!query) return undefined;

  const payload: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(query)) {
    if (key === 'tipo_do_evento') payload.TIPO_DO_EVENTO = value;
    else if (key === 'categoria') payload.CATEGORIA = value;
    else payload[key] = value;
  }
  return payload;
}

export function normalizeCalculoBaseRow(raw: Record<string, unknown>): CalculoBaseRow {
  const tipo = fixMojibake(String(raw.tipo_do_evento ?? raw.TIPO_DO_EVENTO ?? ''));
  const categoriaRaw = fixMojibake(String(raw.categoria ?? raw.CATEGORIA ?? ''));
  const familia = fixMojibake(String(raw.familia ?? raw.FAMILIA ?? ''));

  return {
    id: Number(raw.id),
    tipo_do_evento: tipo,
    categoria: normalizeCarolinoCategoria(categoriaRaw),
    familia,
    variacao: Number(raw.variacao ?? raw.VARIACAO ?? 0),
    qtd_un_por_pes: Number(raw.qtd_un_por_pes ?? raw.QTD_UN_POR_PES ?? 0),
  };
}

export interface CreatePortalCategoryBody {
  slug: string;
  label: string;
  display_order?: number;
  active?: boolean;
}

export interface PatchPortalCategoryBody {
  slug?: string;
  label?: string;
  display_order?: number;
  active?: boolean;
}

export function backofficeRoleLabel(role: Exclude<BackofficeRole, null>): string {
  return role === 'admin_backoffice' ? 'Admin Backoffice' : 'Backoffice (leitura)';
}
